import { GetHardwareMonitoringStatusBody } from "@intelligentlilli/api-layer";
import { INITAL_SENSORS } from "Services/Constants";
import { AugmentDeviceData } from "./types";
import { useAppSelector } from "State/hooks";

// Build a lookup map from sensor label (lowercase) → { locationOptions, locationRequired }
// so that useAugmentDeviceData never duplicates options already defined in INITAL_SENSORS.
const locationDataByLabel = Object.fromEntries(
  INITAL_SENSORS.devices
    .filter((d) => d.locationOptions || d.locationRequired !== undefined)
    .map((d) => [
      d.label.toLowerCase(),
      {
        locationOptions: d.locationOptions ?? [],
        locationRequired: d.locationRequired ?? false,
      },
    ])
);

export const useAugmentDeviceData = (
  devices: GetHardwareMonitoringStatusBody | null,
  hubId: number
): AugmentDeviceData[] => {
  const serviceUser = useAppSelector((state) =>
    state.serviceUsers.find((su) => su.id === hubId)
  );

  const sensorLocations = serviceUser?.serviceUserProperties?.sensorLocations;

  const parsedSensorLocations = sensorLocations
    ? JSON.parse(sensorLocations)
    : null;

  // handle if empty array
  if (!devices || devices.length === 0) {
    return [];
  }

  const additionalData = [
    {
      nameRegex: new RegExp("hub", "i"),
      displayName: "Lilli hub",
      img: "/images/installation/hub/hub.png",
      defaultName: "Hub",
      label: "H1",
      type: "Hub",
    },
    {
      defaultName: "Smart Plug Mini UK",
      nameRegex: new RegExp("Kitchen/[\\w]+/Power$", "i"), // Kitchen/<anything>/Power
      displayName: "Smart plug",
      img: "/images/installation/sensors/plug/plug_thumb.png",
      alt: "plug icon",
      label: "P1",
      type: "Power",
      ...locationDataByLabel["p1"],
    },
    {
      defaultName: "Motion Sensor Mini",
      nameRegex: new RegExp("Bathroom/Wall/Motion$", "i"), // Bathroom/Wall/Motion
      displayName: "Bathroom sensor",
      description:
        "This motion sensor is to be placed at any height in the bathroom. Be sure the sensor is as close to the bathing area as possible and not obstructed so it has as much view of the room as possible.",
      img: "/images/installation/sensors/motion/motion_thumb.png",
      alt: "motion icon",
      id: "Bathroom",
      label: "M2",
      type: "Motion",
      ...locationDataByLabel["m2"],
    },
    {
      defaultName: "Motion Sensor Mini",
      nameRegex: new RegExp("Bedroom/Wall/Motion$", "i"), //Bedroom/Wall/Motion,
      displayName: "Bedroom sensor",
      description:
        "This motion sensor is ideally placed outside of the bedroom door where it can capture entering/exiting of the bedroom but avoiding any other in-home movement, as much as possible.",
      img: "/images/installation/sensors/motion/motion_thumb.png",
      alt: "motion icon",
      id: "Bedroom",
      label: "M3",
      type: "Motion",
      ...locationDataByLabel["m3"],
    },
    {
      defaultName: "Motion Sensor Mini",
      nameRegex: new RegExp("Hall/Wall/Motion$", "i"), //Hall/Wall/Motion,
      displayName: "Movement sensor",
      description:
        "This motion sensor should be used to monitor Movement around the home. The sensor works best facing an area in the home that has high traffic throughout the day and should be placed above waist height.",
      img: "/images/installation/sensors/motion/motion_thumb.png",
      alt: "motion icon",
      id: "Hall",
      label: "M1",
      type: "Motion",
      ...locationDataByLabel["m1"],
    },
    {
      defaultName: "Window Sensor",
      nameRegex: new RegExp("Kitchen/Fridge/Door$", "i"), // Kitchen/Fridge/Door
      displayName: "Fridge sensor",
      img: "/images/installation/sensors/door/door_thumb.png",
      alt: "door icon",
      label: "D2",
      type: "Door",
      ...locationDataByLabel["d2"],
    },
    {
      defaultName: "Window Sensor",
      nameRegex: new RegExp(
        "[\\w]+/Front/Door$|[\\w]+/Back/Door$|[\\w]+/Main/Door$",
        "i"
      ), //<anything>/[Front|Back|Main]/Door
      displayName: "Main door sensor",
      img: "/images/installation/sensors/door/door_thumb.png",
      alt: "door icon",
      label: "D1",
      type: "Door",
      ...locationDataByLabel["d1"],
    },
    {
      defaultName: "Window Sensor",
      nameRegex: new RegExp("Hall/Secondary/Door$", "i"), // Hall/Secondary/Door
      displayName: "Secondary door sensor",
      img: "/images/installation/sensors/door/door_thumb.png",
      alt: "door icon",
      label: "D3",
      type: "Door",
      locationRequired: true,
      locationOptions: [
        { value: "back door", label: "Back door" },
        { value: "balcony door", label: "Balcony door" },
        { value: "garden door", label: "Garden door" },
        { value: "garage door", label: "Garage door" },
        { value: "side door", label: "Side door" },
      ],
    },
    {
      defaultName: "Vibration Sensor",
      nameRegex: new RegExp("[\\w]+/[\\w]+/Vibration$", "i"), //<anything>/<anything>/Vibration
      displayName: "Vibration sensor",
      img: "/images/installation/sensors/vibration/vibration_thumb.png",
      alt: "vibration sensor icon",
      locationRequired: false,
    },
    {
      defaultName: "Air Quality Sensor",
      nameRegex: new RegExp("[\\w]+/[\\w]+/VOC", "i"), //<anything>/<anything>/VOC  (which stands for volatile organic compounds)
      displayName: "Air quality sensor",
      img: "/images/installation/sensors/airquality/airquality_thumb.png",
      alt: "air quality sensor icon",
      locationRequired: false,
    },
    {
      defaultName: "Humidity Sensor",
      nameRegex: new RegExp("[\\w]+/[\\w]+/Humidity", "i"), //<anything>/<anything>/Humidity
      displayName: "Humidity sensor",
      img: "/images/installation/sensors/humidity/humidity_thumb.png",
      alt: "humidity sensor icon",
      locationRequired: false,
    },
  ];

  const augmentedSensorData = devices?.map((sensor) => {
    const additionalSensorData = additionalData.find((data) =>
      data.nameRegex.test(sensor.location ?? sensor.child_device_internal_id)
    );

    let sensorLocationName = null;

    if (additionalSensorData && parsedSensorLocations) {
      const matchedLocation = Object.keys(parsedSensorLocations).find(
        (location) => additionalSensorData.nameRegex.test(location)
      );

      if (matchedLocation) {
        sensorLocationName = parsedSensorLocations[matchedLocation];
      }
    }

    return {
      ...sensor,
      ...(additionalSensorData ?? {}),
      sensorLocation: sensorLocationName,
    };
  });

  return augmentedSensorData as AugmentDeviceData[];
};
